import { useEffect, useMemo, useRef, useState } from 'react';
import { findUniqueMatch, type PageReviewRow } from '@oysterjs/smartfill-contracts';
import { useSidebarSession } from '../bridge/session';
import { displayError, type DisplayError } from '../bridge/errors';
import { type FlatDocumentProperty } from '../buckets/document';
import { PencilIcon } from './Icons';
import { InlineError } from './SidebarState';

export function PageReviewCard({
  row,
  tabContextId,
  teachOptions,
  showTechnicalProblems
}: {
  row: PageReviewRow;
  tabContextId: string;
  teachOptions: FlatDocumentProperty[];
  showTechnicalProblems: boolean;
}) {
  const { client, snapshotRevision, applyPageSession } = useSidebarSession();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  // Null until the user types here, so an edit made in the form while the card
  // is open shows up in the draft instead of being overwritten by Save.
  const [draftValue, setDraftValue] = useState<string | null>(null);
  const [neverFill, setNeverFill] = useState(false);
  const [leftBlank, setLeftBlank] = useState(false);
  const [remembered, setRemembered] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DisplayError | null>(null);
  const rememberedTimer = useRef<number | null>(null);
  const saving = useRef(false);

  useEffect(
    () => () => {
      if (rememberedTimer.current !== null) window.clearTimeout(rememberedTimer.current);
    },
    []
  );

  const label = row.label || 'Field';
  const draft = draftValue ?? row.value;
  const reason = rowReason(row, showTechnicalProblems);

  const beginEditing = () => {
    setDraftValue(null);
    setNeverFill(false);
    setEditing(true);
    setError(null);
    void client.request('review.focusField', { fieldId: row.fieldId }).catch((requestError) => {
      setError(displayError(requestError, 'Could not focus this field on the page.'));
    });
  };

  // A typed value that equals exactly one bucket property teaches what this
  // field means; re-confirming the property the resolver matched teaches nothing.
  const hintProperty = useMemo(() => {
    if (neverFill) return null;
    const match = findUniqueMatch(teachOptions, draft);
    return match && match.propertyId !== (row.propertyId ?? '') ? match : null;
  }, [neverFill, draft, teachOptions, row.propertyId]);

  const save = async () => {
    if (saving.current) return;
    saving.current = true;
    const teach = neverFill
      ? { action: 'SKIP' as const }
      : hintProperty
        ? { action: 'HINT' as const, propertyId: hintProperty.propertyId }
        : undefined;
    const value = neverFill ? '' : draft;
    setBusy(true);
    setError(null);
    try {
      const sinceRevision = snapshotRevision('pageSession');
      const result = await client.request('review.saveEdit', {
        fieldId: row.fieldId,
        value,
        ...(teach ? { teach } : {})
      });
      if (!applyPageSession(result, { expectedTabContextId: tabContextId, sinceRevision })) return;
      setSaved(true);
      setLeftBlank(neverFill);
      setEditing(false);
      if (teach?.action === 'HINT') {
        setRemembered(true);
        if (rememberedTimer.current !== null) window.clearTimeout(rememberedTimer.current);
        rememberedTimer.current = window.setTimeout(() => {
          setRemembered(false);
          rememberedTimer.current = null;
        }, 1_800);
      }
    } catch (requestError) {
      setError(displayError(requestError, 'Could not save this field to the page.'));
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };

  // The save returns only the page, so the rule is found again by its field
  // label: labels come from the same field on both sides, and the newest skip
  // with this label is the one just saved.
  const undoNeverFill = async () => {
    setBusy(true);
    setError(null);
    try {
      const rule = (await client.request('rules.list', {}))
        .filter((candidate) => candidate.action === 'SKIP' && candidate.fieldLabel === label)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
      if (rule) await client.request('rules.clear', { ruleId: rule.id });
      setLeftBlank(false);
      setSaved(false);
    } catch (requestError) {
      setError(displayError(requestError, 'Could not undo this. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      className={`review-card${row.focused ? ' review-card--focused' : ''}`}
      aria-label={label}
    >
      {editing ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <label
            className="editor-label editor-label--value"
            htmlFor={`field-value-${row.fieldId}`}
          >
            Value
          </label>
          <input
            autoFocus
            id={`field-value-${row.fieldId}`}
            className="review-input review-input--value"
            value={neverFill ? '' : draft}
            placeholder={neverFill ? 'Left blank' : undefined}
            disabled={neverFill}
            onChange={(event) => setDraftValue(event.target.value)}
          />
          <label className="preference-toggle preference-toggle--inline">
            <span>
              <strong>Don’t fill this field</strong>
              <small>Cara leaves it blank on this form from now on.</small>
            </span>
            <input
              type="checkbox"
              checked={neverFill}
              disabled={busy}
              onChange={(event) => setNeverFill(event.target.checked)}
            />
          </label>
          {hintProperty && (
            <p className="editor-hint">
              Cara will remember this field means “{hintProperty.displayLabel}” here.
            </p>
          )}
          {error && <InlineError message={error.message} />}
          <div className="review-card__actions">
            <button
              type="button"
              className="button button--secondary button--pill"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button button--primary button--pill"
              disabled={busy}
              aria-busy={busy}
            >
              {neverFill ? 'Save' : 'Save to page'}
            </button>
          </div>
        </form>
      ) : (
        <div className="review-card__reading">
          <div className="review-card__copy">
            <div className="review-card__label">
              <span>{label}</span>
              {remembered && <span className="remembered">Remembered</span>}
            </div>
            <div className="review-card__value">
              {leftBlank ? (
                <>
                  Left blank ·{' '}
                  <button
                    type="button"
                    className="review-card__undo"
                    disabled={busy}
                    onClick={() => void undoNeverFill()}
                  >
                    Undo
                  </button>
                </>
              ) : (
                row.value || 'Empty'
              )}
            </div>
            {reason && !saved && <span className="review-card__reason">{reason}</span>}
            {error && <span className="review-card__error">{error.message}</span>}
          </div>
          {leftBlank ? null : saved ? (
            <span className="saved-marker">
              <span />
              Saved
            </span>
          ) : (
            <button
              type="button"
              className="review-edit-button"
              aria-label={`Edit ${label}`}
              onClick={beginEditing}
            >
              <PencilIcon />
            </button>
          )}
        </div>
      )}
    </article>
  );
}

// Reasons that tell the user something to do or check. The rest describe
// Cara's own mechanics: an empty field needs filling either way, and a value
// Cara could not confirm only needs checking.
const ACTIONABLE_REASONS = new Set([
  'VALIDATION_FAILED',
  'VALUE_CHANGED_AFTER_WRITE',
  'INPUT_REJECTED',
  'UNSUPPORTED_OPTION_ACTION',
  'SOURCE_CONTEXT_CHANGED',
  'AMBIGUOUS_OPTION',
  'OPTION_NOT_FOUND',
  'PREFILLED',
  'USER_EDITED',
  'INVALID_DATE',
  'VALUE_TOO_LONG',
  'ASSUMED_OPTION',
  'ASSUMED_DAY',
  'LOW_CONFIDENCE',
  'UNBOUND_REPEATED_SOURCE'
]);

function rowReason(row: PageReviewRow, showTechnicalProblems: boolean): string | null {
  const reason = row.writeReason ?? row.reason;
  if (!reason) return null;
  if (showTechnicalProblems || ACTIONABLE_REASONS.has(reason)) return reasonLabel(reason);
  return row.state === 'REVIEW' ? 'Check this answer' : null;
}

function reasonLabel(reason: string): string {
  switch (reason) {
    case 'NO_MATCH':
      return 'No confident match found';
    case 'VALUE_NOT_ACCEPTED':
    case 'CONTROL_ERROR':
      return 'Could not verify that the page accepted this value';
    case 'VALIDATION_FAILED':
      return 'The page reports a validation error. Please review this answer';
    case 'VALIDATION_PENDING':
      return 'The page is still checking this answer';
    case 'VALUE_CHANGED_AFTER_WRITE':
      return 'The page changed this answer after Cara filled it. Please review it';
    case 'INPUT_REJECTED':
      return 'The page refused this entry. Complete it manually';
    case 'METHOD_UNAVAILABLE':
    case 'RETRY_EXHAUSTED':
      return 'Cara could not complete this field safely. Complete it manually';
    case 'WRITE_TIMEOUT':
      return 'This field took too long. Review it before filling again';
    case 'UNSUPPORTED_OPTION_ACTION':
      return 'This choice requires a manual action';
    case 'FORM_CHANGED':
      return 'The form changed; this answer needs to be matched again';
    case 'SOURCE_CONTEXT_CHANGED_RESTORED':
      return 'The question changed; Cara removed its previous answer because it no longer matched';
    case 'SOURCE_CONTEXT_CHANGED':
      return 'The question changed; Cara kept its previous answer for you to review.';
    case 'CONTROL_CHANGED':
    case 'MISSING_BINDING':
    case 'CANCELLED':
      return 'This field changed before the fill finished';
    case 'OPTIONS_TIMEOUT':
    case 'OPTIONS_INCOMPLETE':
      return 'The page did not expose a matching choice';
    case 'AMBIGUOUS_OPTION':
      return 'More than one choice matches';
    case 'PREFILLED':
    case 'USER_EDITED':
      return 'Your existing value was left unchanged';
    case 'INVALID_DATE':
      return 'The date does not fit the format requested by this field';
    case 'VALUE_TOO_LONG':
      return 'The proposed value exceeds this field’s character limit';
    case 'OPTION_NOT_FOUND':
      return 'No exact matching choice on the page';
    case 'OPTION_MISMATCH':
      return 'No matching option on the page';
    case 'ASSUMED_OPTION':
      return 'Cara picked the closest choice. Please check it';
    case 'ASSUMED_DAY':
      return 'The client file gives only the month, so Cara used the 1st. Please check it';
    case 'LOW_CONFIDENCE':
      return 'Cara is not confident about this match';
    case 'UNBOUND_REPEATED_SOURCE':
      return 'Cara could not tell which record this row is for';
    case 'ENTITY_CONSUMED':
      return 'This record was already placed on an earlier page';
    case 'INCOMPATIBLE_VALUE':
      return 'The saved value does not fit this field';
    default:
      return 'Needs a look';
  }
}
